import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { isValidImageFile } from '../utils/imageConverter';

const { FiUpload, FiImage, FiAlertCircle } = FiIcons;

const FileUpload = ({ onFileSelect, disabled }) => {
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const error = rejectedFiles[0].errors[0];
      onFileSelect(null, `Ungültige Datei: ${error.message}`);
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;

    if (!isValidImageFile(file)) {
      onFileSelect(null, 'Bitte wähle eine gültige Bilddatei (JPEG, PNG, WebP, GIF, BMP, TIFF, AVIF, SVG)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB
      onFileSelect(null, 'Dateigröße muss kleiner als 10MB sein');
      return;
    }

    onFileSelect(file, null);
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.gif', '.bmp', '.tiff', '.avif', '.svg']
    },
    maxFiles: 1,
    disabled
  });

  return (
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-300
          ${isDragActive 
            ? 'border-primary-500 bg-primary-50' 
            : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center space-y-4">
          <motion.div
            animate={isDragActive ? { scale: 1.1 } : { scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <SafeIcon 
              icon={isDragActive ? FiImage : FiUpload} 
              className="text-4xl text-primary-400" 
            />
          </motion.div>
          <div>
            <p className="text-lg font-medium text-primary-600">
              {isDragActive ? 'Lass dein Bild hier fallen' : 'Bild hochladen'}
            </p>
            <p className="text-sm text-primary-500 mt-1">
              Ziehen und ablegen oder klicken zum Auswählen
            </p>
            <p className="text-xs text-primary-400 mt-2">
              Unterstützt: JPEG, PNG, WebP, GIF, BMP, TIFF, AVIF, SVG (max 10MB)
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FileUpload;